import {
  NgModule,
  ErrorHandler,
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  NO_ERRORS_SCHEMA,
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { RouterModule } from "@angular/router";
import { ReactiveFormsModule } from "@angular/forms";
import { PagesModule } from '#pages/pages.module';
import {
  AppComponent,
  GlobalErrorHandler,
  VersionComponent,
  HeaderComponent,
  FooterComponent,
  WelcomeComponent,
  LoadingIndicatorComponent,
  FeedbackComponent,
} from "./";
import { AppRoutingModule } from "./app.routing.module";
import { AuthService, LoadingIndicatorService } from "#services/shared";
import { SharedModule } from "#services/shared/shared.module";
import { ComponentsModule } from "#components/components.module";
import { environment } from "#environments/environment";
import { firstValueFrom, of } from "rxjs";
import { AuthInterceptor } from "#services/interceptors";
import { ToastModule } from "primeng/toast";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { MessageService, ConfirmationService } from "primeng/api";
import { BlockUIModule } from "primeng/blockui";
import { DialogModule } from "primeng/dialog";
import { ServerErrorInterceptor } from "#services/interceptors/ServerErrorInterceptor";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { WebpackTranslateLoader } from "./webpack-translate-loader";
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from "@azure/msal-angular";
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { UserService } from "#services/api";
import { ApplicationinsightsAngularpluginErrorService } from "@microsoft/applicationinsights-angularplugin-js";

import { providePrimeNG } from "primeng/config";
import Aura from "@primeng/themes/aura";
import ChevTheme from "assets/css/themes/preset";
import OktaAuth from "@okta/okta-auth-js";
import { OktaAuthModule } from "@okta/okta-angular";
export const protectedResourceMap: [string, string[]][] = [
  ["https://graph.microsoft.com/v1.0/me", ["user.read"]],
];

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

// A single shared MSAL instance. MSALInstanceFactory is referenced in two places
// (MsalModule.forRoot(...) and the MSAL_INSTANCE provider). If it built a new
// PublicClientApplication on every call, the app would run TWO MSAL instances
// bound to the same sessionStorage: MsalBroadcastService/inProgress$ would track
// one instance while MsalService.login()/handleRedirectPromise() ran on the
// other, so the login page's inProgress$ never settled and kept firing sign-in
// (the repeated login redirects). Memoizing guarantees one instance everywhere.
let msalInstance: IPublicClientApplication | undefined;

export function MSALInstanceFactory(): IPublicClientApplication {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: environment.msalConfig.auth.clientId,
        authority: environment.msalConfig.auth.authority,
        redirectUri: "/",
        postLogoutRedirectUri: "/",
      },
      cache: {
        cacheLocation: "sessionStorage",
      },
      system: {
        allowNativeBroker: false, // Disables WAM Broker
        loggerOptions: {
          loggerCallback,
          logLevel: LogLevel.Info,
          piiLoggingEnabled: false,
        },
      },
    });
  }
  return msalInstance;
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set("https://graph.microsoft.com/v1.0/me", [
    "user.read",
    environment.msalConfig.auth.user_impersonation,
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ["user.read", environment.msalConfig.auth.user_impersonation],
    },
    loginFailedRoute: "/login-failed",
  };
}

const oktaAuth = new OktaAuth({
  clientId: environment.oktaConfig.clientId,
  issuer: environment.oktaConfig.issuer,
  redirectUri: environment.oktaConfig.redirectUri,
  scopes: environment.oktaConfig.scopes,
});

export function loadUserInfo(userSvc: UserService) {
  return () =>
    userSvc.loadCurrentUserInfo().then(
      (user) => {
        return user;
      },
      (error) => {
        return of(error);
      }
    );
}

// Runs before Angular routing/guards. It (1) initializes MSAL and consumes any
// redirect response, then (2) silently signs the user in from the EXISTING Azure
// AD browser session via ssoSilent - no UI, no second login. This is what lets
// the Scheduler open (e.g. from a TA Pro deep link) and authenticate the already
// signed-in user automatically. When an account is established, the user's roles
// are loaded (AuthService.getUserData) so the route guards have everything they
// need and never bounce to the login page. If no Azure session exists (or silent
// sign-in needs interaction) it fails quietly and the normal interactive login
// flow takes over - bootstrap is never blocked.
export function initializeMsal(
  msalService: MsalService,
  authService: AuthService
): () => Promise<void> {
  return async () => {
    try {
      await msalService.instance.initialize();
      await msalService.instance.handleRedirectPromise();

      let account =
        msalService.instance.getActiveAccount() ??
        msalService.instance.getAllAccounts()[0];

      if (!account) {
        try {
          const result = await msalService.instance.ssoSilent({
            scopes: [environment.msalConfig.auth.user_impersonation],
          });
          account = result.account;
        } catch {
          // Silent SSO needs interaction (no/ambiguous Azure session). Leave it
          // to the interactive login flow; do not block bootstrap.
        }
      }

      if (account) {
        msalService.instance.setActiveAccount(account);
        // Populate the access token + role flags (SystemAdmin/Scheduler) in
        // sessionStorage so the route guards pass without a second login.
        await firstValueFrom(authService.getUserData());
      }
    } catch (error) {
      console.error("MSAL initialization failed", error);
    }
  };
}

// okta code

@NgModule({
  declarations: [
    AppComponent,
    VersionComponent,
    HeaderComponent,
    FooterComponent,
    WelcomeComponent,
    LoadingIndicatorComponent,
    FeedbackComponent,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  imports: [
    BrowserModule,
    RouterModule,
    AppRoutingModule,
    SharedModule,
    ReactiveFormsModule,
    ComponentsModule,
    ToastModule,
    ConfirmDialogModule,
    BlockUIModule,
    DialogModule,
    PagesModule,
    FontAwesomeModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: WebpackTranslateLoader,
      },
    }),
    MsalModule.forRoot(
      MSALInstanceFactory(),
      MSALGuardConfigFactory(),
      MSALInterceptorConfigFactory()
    ),

    OktaAuthModule.forRoot({
      oktaAuth,
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: environment.useAppInsights
        ? ApplicationinsightsAngularpluginErrorService
        : ErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true,
    },
    providePrimeNG({
      theme: {
        preset: ChevTheme,
        options: {
          darkModeSelector: false || "none",
        },
      },
    }),
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MsalService, AuthService],
      multi: true,
    },
    LoadingIndicatorService,
    MessageService,
    ConfirmationService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
